import { useCallback, useState } from 'react';
import { APIProvider, MapMouseEvent } from '@vis.gl/react-google-maps';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { Place } from './model/Station';
import { Connector } from './model/Connector';
import { Route, Segment } from './model/Route';
import { Point } from './model/Point';
import { RouteOptions } from './model/RouteOptions';
import { ControlPanel } from './widgets/ControlPanel';
import { GoogleMap } from './widgets/GoogleMap';
import { StationBottomSheet } from './widgets/StationBottomSheet';
import ContactButton from './widgets/ContactButton';
import { isMobile } from 'react-device-detect';

const MAX_WAYPOINTS = 3;
const LANG = 'ru';

const App = () => {
  const GOOGLE_MAP_API_KEY = process.env.REACT_APP_GOOGLE_MAP_API_KEY!;
  const ROUTE_API_URL = process.env.REACT_APP_ROUTE_API_URL!;

  const [message, setMessage] = useState<string | undefined>(undefined);

  const [selectedPlace, setSelectedPlace] = useState<Place | undefined>();
  const [selectedSegment, setSelectedSegment] = useState<Segment | undefined>();

  const [controlPanelExpanded, setControlPanelExpanded] = useState(true);
  const [routeLoading, setRouteLoading] = useState(false);
  const [route, setRoute] = useState<Route | undefined>();
  const [waypoints, setWaypoints] = useState<Point[]>([]);
  const [showOptionalStations, setShowOptionalStations] = useState<boolean>(false);

  const handleMarkerClick = (place: Place, segment: Segment) => {
    setSelectedPlace(place);
    setSelectedSegment(segment);
  }

  const [startPoint, setStartPoint] = useState<Point | undefined>(undefined);
  const [endPoint, setEndPoint] = useState<Point | undefined>(undefined);

  const fetchRoute = useCallback(async (pointA: Point, pointB: Point, selectedConnectors: Connector[], carRange: number, options: RouteOptions) => {
    setRouteLoading(true);
    // console.log('fetchRoute started', pointA, pointB, selectedConnectors)

    try {
      const connectorTypes = selectedConnectors.map(c => c.name);

      const routesApiUrl = `https://routes.googleapis.com/directions/v2:computeRoutes`;
      const intermediates = waypoints.map(p => ({
        location: { latLng: { latitude: p.lat, longitude: p.lng } }
      }));
      const routesResponse = await fetch(routesApiUrl, {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
          "X-Goog-Api-Key": GOOGLE_MAP_API_KEY,
          "X-Goog-FieldMask": "routes.polyline.encodedPolyline",
        },
        body: JSON.stringify({
          origin: { location: { latLng: { latitude: pointA.lat, longitude: pointA.lng } } },
          destination: { location: { latLng: { latitude: pointB.lat, longitude: pointB.lng } } },
          intermediates: intermediates.length > 0 ? intermediates : undefined,
          travelMode: "DRIVE",
          languageCode: LANG,
        }),
      });
      if (!routesResponse.ok) {
        console.log('unable to get directions from Routes API');
        setMessage('Произошла ошибка во время загрузки маршрута. Сообщите разработчику');
        return;
      }
      const routesData = await routesResponse.json();
      const encodedPolyline = routesData.routes[0].polyline.encodedPolyline;

      const response = await fetch(ROUTE_API_URL, {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
          "x-api-key": "aV7DQh%yZi2^hzYuTfF4dQJF7b*yx"
        },
        body: JSON.stringify({
          route_polyline: encodedPolyline,
          connector_types: connectorTypes,
          car_range: carRange,
          options: options,
        }),
      });
      if (!response.ok) {
        console.log('unable to get route')
        setMessage('Произошла ошибка во время загрузки маршрута. Сообщите разработчику');
        return;
      }
      const data = await response.json();
      setRoute(data);
      if (isMobile) {
        setControlPanelExpanded(false);
      }

      // console.log('fetchRoute finished')
    } catch (error) {
      console.error(error);
      setControlPanelExpanded(true);
      setMessage('Произошла ошибка во время загрузки маршрута. Попробуйте изменить начальную и конечную точки маршрута');
    } finally {
      setRouteLoading(false);
    }
  }, [GOOGLE_MAP_API_KEY, ROUTE_API_URL, waypoints]);

  const handleOnMapClick = useCallback((event: MapMouseEvent) => {
    setSelectedPlace(undefined);
    setSelectedSegment(undefined);
    if (event.detail.latLng !== null) {
      if (!startPoint) {
        setStartPoint(event.detail.latLng);
      } else if (!endPoint) {
        setEndPoint(event.detail.latLng);
      }
    }
  }, [startPoint, endPoint]);

  const handleLoadRoute = (carRange: number, connectors: Connector[], options: RouteOptions) => {
    if (!startPoint || !endPoint) {
      return;
    }
    setSelectedPlace(undefined);
    setRoute(undefined);
    fetchRoute(startPoint, endPoint, connectors, carRange, options);
  }

  const handleReset = () => {
    setRoute(undefined);
    setWaypoints([]);
    setStartPoint(undefined);
    setEndPoint(undefined);
    setSelectedPlace(undefined);
    // setSelectedConnectors([])
    // setChargesCount(undefined);
  };

  const handleSwapStartEndPoints = useCallback(() => {
    const tempEndPoint = endPoint;
    setEndPoint(startPoint);
    setStartPoint(tempEndPoint);
  }, [startPoint, endPoint]);

  const handleAddWaypoint = useCallback((point: Point) => {
    if (waypoints.length === MAX_WAYPOINTS) {
      setMessage(`Максимальное количество промежуточных точек составляет ${MAX_WAYPOINTS} шт.`)
      return;
    }
    setWaypoints([...waypoints, point])
  }, [waypoints]);

  const handleDeleteWaypoint = useCallback((point: Point) => {
    setWaypoints(waypoints.filter(p => p !== point))
  }, [waypoints]);

  const handleControlPanelToggleExpand = () => {
    setControlPanelExpanded((prevExpanded) => !prevExpanded);
  };

  return (
    <>
      <Box>
        <ControlPanel
          expanded={controlPanelExpanded}
          route={route}
          isLoading={routeLoading}
          startPoint={startPoint}
          endPoint={endPoint}
          waypoints={waypoints}
          onStartPointChange={setStartPoint}
          onEndPointChange={setEndPoint}
          onLoadRouteClick={handleLoadRoute}
          onSwapStartEndPointsClick={handleSwapStartEndPoints}
          onDeleteWaypointClick={handleDeleteWaypoint}
          onResetClick={handleReset}
          onExpandClick={handleControlPanelToggleExpand}
          onShowOptionalStationsChange={show => { setShowOptionalStations(show) }} />

        <Box
          style={{
            position: 'absolute',
            bottom: '15px',
            right: '10px',
            zIndex: 10,
          }}>
          <ContactButton />
        </Box>
        <APIProvider apiKey={GOOGLE_MAP_API_KEY}>
          <GoogleMap
            route={route}
            showOptionalStations={showOptionalStations}
            startPoint={startPoint}
            endPoint={endPoint}
            waypoints={waypoints}
            onMapClick={handleOnMapClick}
            onMarkerClick={handleMarkerClick}
            onAddPointToRoute={handleAddWaypoint}
            onStartPointDrag={setStartPoint}
            onEndPointDrag={setEndPoint} />

        </APIProvider>
      </Box>

      {selectedPlace && (
        <StationBottomSheet
          place={selectedPlace}
          segment={selectedSegment}
          onClose={() => setSelectedPlace(undefined)}
        />
      )}

      <Dialog
        open={message !== undefined}
        onClose={() => setMessage(undefined)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Сообщение</DialogTitle>
        <DialogContent>
          <Typography>{message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant='contained' onClick={() => setMessage(undefined)}>
            Закрыть
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
};

export default App;
